import React from "react";

import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";

import styles from "./VotingCenterReportsFacets.module.styl";

export const DEFAULT_VOTING_CENTER_REPORT_FILTERS = Object.freeze({
  disabledCenters: "exclude"
});

const DISABLED_CENTER_STATES = Object.freeze([
  { value: "exclude", label: "filter.votingCenterHide" },
  { value: "include", label: "filter.votingCenterInclude" },
  { value: "only", label: "filter.votingCenterOnly" }
]);

const normalizeChoice = (value, allowed, fallback) => {
  return allowed.includes(value) ? value : fallback;
};

export const normalizeVotingCenterReportFilters = (value) => {
  const input = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};

  return {
    disabledCenters: normalizeChoice(
      input.disabledCenters,
      ["exclude", "include", "only"],
      DEFAULT_VOTING_CENTER_REPORT_FILTERS.disabledCenters
    )
  };
};

export const isDefaultVotingCenterReportFilters = (value) => {
  const filters = normalizeVotingCenterReportFilters(value);
  return filters.disabledCenters === DEFAULT_VOTING_CENTER_REPORT_FILTERS.disabledCenters;
};

export const getVotingCenterReportActiveFilterCount = (value) => {
  return isDefaultVotingCenterReportFilters(value) ? 0 : 1;
};

export const buildVotingCenterReportBackendFilters = (value) => {
  const filters = normalizeVotingCenterReportFilters(value);

  if (filters.disabledCenters === "include") {
    return {
      status: ["ENABLED", "DISABLED"]
    };
  }

  if (filters.disabledCenters === "only") {
    return {
      status: ["DISABLED"]
    };
  }

  return {
    status: ["ENABLED"]
  };
};

export const loadVotingCenterReportFacetCounts = async ({ search, filters }) => {
  return await rest.request(
    getServiceUri() + "reporting/voting-center-reports/facets",
    "POST",
    {
      search: String(search || "").trim(),
      filters: buildVotingCenterReportBackendFilters(filters)
    }
  );
};

const countFor = (counts, path, fallback = 0) => {
  let current = counts || {};

  for (const key of path) {
    if (!current || typeof current !== "object") {
      return fallback;
    }

    current = current[key];
  }

  const number = Number(current);
  return Number.isFinite(number) ? number : fallback;
};

const Chip = ({ label, onRemove }) => (
  <button
    type="button"
    className={styles.chip}
    onClick={onRemove}
  >
    <span>{label}</span>
    <span className={styles.chipClose}>×</span>
  </button>
);

const DisabledCenterFilter = ({ filters, counts, onSet }) => {
  const selected = filters.disabledCenters;
  const disabledCount = countFor(counts, ["status", "DISABLED"]);

  return (
    <div className={styles.exclusionCompactRow}>
      <div className={styles.exclusionCompactMeta}>
        <span className={styles.exclusionCompactName}>
          {t("filter.disabledCenters")}
        </span>
        <span className={styles.exclusionCompactCount}>
          {disabledCount}
        </span>
      </div>

      <div className={styles.segmentedControl}>
        {DISABLED_CENTER_STATES.map((state) => (
          <button
            key={state.value}
            type="button"
            className={
              selected === state.value
                ? `${styles.segmentedButton} ${styles.segmentedButtonSelected}`
                : styles.segmentedButton
            }
            onClick={() => onSet(state.value)}
          >
            {t(state.label)}
          </button>
        ))}
      </div>
    </div>
  );
};

export const VotingCenterReportsFacetButton = ({ controller }) => {
  const activeCount = controller?.activeCount || 0;

  return (
    <button
      type="button"
      className={
        activeCount
          ? `${styles.filterButton} ${styles.filterButtonActive}`
          : styles.filterButton
      }
      onClick={controller.toggleDrawer}
    >
      <span className={styles.filterButtonIcon}>
        <TuneIcon fontSize="inherit" />
      </span>
      <span className={styles.filterButtonLabel}>
        {t("common.filters")}
      </span>
      {activeCount ? (
        <span className={styles.filterButtonBadge}>
          {activeCount}
        </span>
      ) : null}
    </button>
  );
};

export const VotingCenterReportsFacetChips = ({ controller }) => {
  const filters = normalizeVotingCenterReportFilters(controller?.value);

  if (isDefaultVotingCenterReportFilters(filters)) {
    return null;
  }

  const state = DISABLED_CENTER_STATES.find(
    (item) => item.value === filters.disabledCenters
  );

  const stateLabel = state ? t(state.label) : filters.disabledCenters;

  return (
    <div className={styles.chipsBar}>
      <Chip
        label={`${t("filter.disabledCenters")}: ${stateLabel}`}
        onRemove={() => controller.reset()}
      />

      <button
        type="button"
        className={styles.clearAll}
        onClick={controller.reset}
      >
        {t("common.clearAll")}
      </button>
    </div>
  );
};

const VotingCenterReportsFacets = ({ controller }) => {
  const filters = normalizeVotingCenterReportFilters(controller?.value);
  const counts = controller?.counts || {};

  return (
    <div className={styles.drawer}>
      <div className={styles.drawerHeader}>
        <div>
          <div className={styles.drawerTitle}>
            {t("common.filters")}
          </div>
          <div className={styles.drawerSubtitle}>
            {controller.countsLoading
              ? t("common.updatingCounts")
              : t("filter.refineVotingCenters")}
          </div>
        </div>

        <button
          type="button"
          className={styles.closeButton}
          onClick={controller.closeDrawer}
          aria-label={t("common.closeFilters")}
        >
          <CloseIcon fontSize="inherit" />
        </button>
      </div>

      <div className={styles.drawerBody}>
        <section className={styles.section}>
          <div className={styles.sectionTitle}>
            {t("filter.availability")}
          </div>

          <div className={styles.exclusionHelp}>
            {t("filter.disabledCentersHelp")}
          </div>

          <DisabledCenterFilter
            filters={filters}
            counts={counts}
            onSet={(state) => controller.patchValue({ disabledCenters: state })}
          />
        </section>
      </div>

      <div className={styles.drawerFooter}>
        <button
          type="button"
          className={styles.secondaryAction}
          onClick={controller.reset}
          disabled={controller.activeCount === 0}
        >
          <RestartAltIcon fontSize="inherit" />
          <span>{t("common.resetFilters")}</span>
        </button>

        <button
          type="button"
          className={styles.primaryAction}
          onClick={controller.closeDrawer}
        >
          {t("common.done")}
        </button>
      </div>
    </div>
  );
};

export const VOTING_CENTER_REPORTS_FACETS = {
  storageKey: "haiti.voting-center-reports.facets.v1",
  overlayLabel: t("filter.votingCenterReportFilters"),
  defaultValue: DEFAULT_VOTING_CENTER_REPORT_FILTERS,
  normalizeValue: normalizeVotingCenterReportFilters,
  isDefaultValue: isDefaultVotingCenterReportFilters,
  getActiveCount: getVotingCenterReportActiveFilterCount,
  loadCounts: loadVotingCenterReportFacetCounts,
  renderButton: (controller) => (
    <VotingCenterReportsFacetButton controller={controller} />
  ),
  renderDrawer: (controller) => (
    <VotingCenterReportsFacets controller={controller} />
  ),
  renderChips: (controller) => (
    <VotingCenterReportsFacetChips controller={controller} />
  )
};

export default VotingCenterReportsFacets;
